<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { inject } from 'vue';
import { useI18n } from 'vue-i18n';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';
import api from 'datatables/api/index.js';

const props = defineProps({
  nodeType: { type: String, required: true },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

const isQuery  = computed(() => props.nodeType === 'datatable_query');
const isInsert = computed(() => props.nodeType === 'datatable_insert');
const isUpdate = computed(() => props.nodeType === 'datatable_update');
const isDelete = computed(() => props.nodeType === 'datatable_delete');
const hasFilter  = computed(() => isQuery.value || isUpdate.value || isDelete.value);
const hasMapping = computed(() => isInsert.value || isUpdate.value);

// ─── Tables ───────────────────────────────────────────────────────────────────
const tables     = ref([]);
const tableSchema = ref([]);

const tableOptions  = computed(() => tables.value.map(tbl => ({ id: tbl.slug, name: tbl.name })));
const tableSelected = computed(() => tableOptions.value.find(opt => opt.id === props.modelValue?.table_slug) ?? null);

async function loadTables() {
  try {
    const { data } = await api.getTables();
    tables.value = data.datatables ?? [];
  } catch { tables.value = []; }
}

async function loadSchema(slug) {
  if (!slug) { tableSchema.value = []; return; }
  try {
    const { data } = await api.getTable(slug);
    tableSchema.value = data.schema ?? [];
  } catch { tableSchema.value = []; }
}

watch(() => props.modelValue?.table_slug, slug => loadSchema(slug), { immediate: true });
watch(tableSchema, schema => update({ table_columns: schema.map(c => c.name) }));
onMounted(loadTables);

const fieldOptions = computed(() => tableSchema.value.map(f => ({ id: f.name, name: f.name })));

function fieldDef(name) { return tableSchema.value.find(f => f.name === name) ?? null; }

// ─── Operators ────────────────────────────────────────────────────────────────
const OPS_TEXT = computed(() => [
  { value: 'equal_to',     label: t('FLOW_BUILDER.COMMON.OP_EQUAL_TO') },
  { value: 'not_equal_to', label: t('FLOW_BUILDER.COMMON.OP_NOT_EQUAL_TO') },
  { value: 'contains',     label: t('FLOW_BUILDER.COMMON.OP_CONTAINS') },
  { value: 'starts_with',  label: t('FLOW_BUILDER.COMMON.OP_STARTS_WITH') },
  { value: 'ends_with',    label: t('FLOW_BUILDER.COMMON.OP_ENDS_WITH') },
  { value: 'is_present',   label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_NOT_EMPTY') },
  { value: 'is_empty',     label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_EMPTY') },
]);
const OPS_NUMBER = computed(() => [
  { value: 'equal_to',                 label: t('FLOW_BUILDER.COMMON.OP_EQUAL_TO') },
  { value: 'not_equal_to',             label: t('FLOW_BUILDER.COMMON.OP_NOT_EQUAL_TO') },
  { value: 'greater_than',             label: t('FLOW_BUILDER.COMMON.OP_GREATER_THAN') },
  { value: 'less_than',                label: t('FLOW_BUILDER.COMMON.OP_LESS_THAN') },
  { value: 'greater_than_or_equal_to', label: t('FLOW_BUILDER.COMMON.OP_GREATER_THAN_OR_EQUAL_TO') },
  { value: 'less_than_or_equal_to',    label: t('FLOW_BUILDER.COMMON.OP_LESS_THAN_OR_EQUAL_TO') },
  { value: 'is_present',               label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_NOT_EMPTY') },
  { value: 'is_empty',                 label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_EMPTY') },
]);
const OPS_BOOL = computed(() => [
  { value: 'equal_to',     label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_IS_TRUE') },
  { value: 'not_equal_to', label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_IS_FALSE') },
]);
const OPS_DATE = computed(() => [
  { value: 'equal_to',                 label: t('FLOW_BUILDER.COMMON.OP_EQUAL_TO') },
  { value: 'not_equal_to',             label: t('FLOW_BUILDER.COMMON.OP_NOT_EQUAL_TO') },
  { value: 'greater_than',             label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_AFTER') },
  { value: 'less_than',                label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_BEFORE') },
  { value: 'greater_than_or_equal_to', label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_FROM') },
  { value: 'less_than_or_equal_to',    label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_UNTIL') },
  { value: 'is_present',               label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_NOT_EMPTY') },
  { value: 'is_empty',                 label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_EMPTY') },
]);
const OPS_SELECT = computed(() => [
  { value: 'equal_to',     label: t('FLOW_BUILDER.COMMON.OP_EQUAL_TO') },
  { value: 'not_equal_to', label: t('FLOW_BUILDER.COMMON.OP_NOT_EQUAL_TO') },
  { value: 'is_present',   label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_NOT_EMPTY') },
  { value: 'is_empty',     label: t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.OP_EMPTY') },
]);

const NO_VALUE_OPS = new Set(['is_present', 'is_empty']);

function isBooleanField(name) { return fieldDef(name)?.type === 'boolean'; }

const QUERY_OPTIONS = computed(() => [
  { id: 'and', name: t('FLOW_BUILDER.COMMON.AND') },
  { id: 'or',  name: t('FLOW_BUILDER.COMMON.OR') },
]);

function opsForField(name) {
  const def = fieldDef(name);
  if (!def) return OPS_TEXT.value;
  if (def.type === 'number') return OPS_NUMBER.value;
  if (def.type === 'boolean') return OPS_BOOL.value;
  if (def.type === 'date' || def.type === 'datetime') return OPS_DATE.value;
  if (def.type === 'select') return OPS_SELECT.value;
  return OPS_TEXT.value;
}
function opOptions(name) { return opsForField(name).map(o => ({ id: o.value, name: o.label })); }
function needsValue(c) { return !NO_VALUE_OPS.has(c.operator) && !isBooleanField(c.field); }

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

function onSelectTable(opt) {
  update({ table_slug: opt?.id ?? null, table_name: opt?.name ?? null, conditions: [], field_mappings: [] });
}

// ─── Conditions ───────────────────────────────────────────────────────────────
const conditions = computed(() => props.modelValue?.conditions ?? []);

function emitConditions(list) {
  const updated = list.map((c, i) => ({
    ...c,
    query_operator: i < list.length - 1 ? (c.query_operator || 'and') : null,
  }));
  update({ conditions: updated });
}

function boolValue(name) { return isBooleanField(name) ? 'true' : ''; }

function addCondition() {
  const first = tableSchema.value[0];
  const name = first?.name ?? '';
  emitConditions([
    ...conditions.value,
    { field: name, operator: opsForField(name)[0]?.value ?? 'equal_to', value: boolValue(name), query_operator: 'and' },
  ]);
}

function removeCondition(i) {
  emitConditions(conditions.value.filter((_, idx) => idx !== i));
}

function patchCondition(i, obj) {
  emitConditions(conditions.value.map((c, idx) => (idx === i ? { ...c, ...obj } : c)));
}

function onSelectConditionField(i, opt) {
  const name = opt?.id ?? '';
  patchCondition(i, { field: name, operator: opsForField(name)[0]?.value ?? 'equal_to', value: boolValue(name) });
}

function onSelectConditionOperator(i, op, field) {
  patchCondition(i, { operator: op, value: boolValue(field) });
}

// ─── Field mappings ───────────────────────────────────────────────────────────
const mappings = computed(() => props.modelValue?.field_mappings ?? []);

const resultVarToken = computed(() => {
  const fallback = isQuery.value ? 'resultado_tabela' : 'novo_id';
  return `{{variables.${props.modelValue?.variable_name || fallback}}}`;
});

function addMapping() {
  const first = tableSchema.value[0];
  update({ field_mappings: [...mappings.value, { field: first?.name ?? '', value: '' }] });
}
function removeMapping(i) {
  update({ field_mappings: mappings.value.filter((_, idx) => idx !== i) });
}
function patchMapping(i, obj) {
  update({ field_mappings: mappings.value.map((m, idx) => (idx === i ? { ...m, ...obj } : m)) });
}

// ─── Variable picker ──────────────────────────────────────────────────────────
const openPickerKey = ref(null);
const varSearch = ref('');

const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q) || v.key.toLowerCase().includes(q));
});

function togglePicker(key) {
  openPickerKey.value = openPickerKey.value === key ? null : key;
  varSearch.value = '';
}

function insertVarInCondition(i, varKey) {
  patchCondition(i, { value: `{{${varKey}}}` });
  openPickerKey.value = null;
}

function insertVarInMapping(i, varKey) {
  patchMapping(i, { value: `{{${varKey}}}` });
  openPickerKey.value = null;
}
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Table selector -->
    <div class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.TABLE') }}</span>
      <MultiselectDropdown
        :options="tableOptions"
        :selected-item="tableSelected"
        :multiselector-placeholder="t('FLOW_BUILDER.COMMON.SELECT_TABLE')"
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH_TABLE')"
        :has-thumbnail="false"
        :hide-search="true"
        @select="onSelectTable"
      />
    </div>

    <template v-if="modelValue?.table_slug">

      <!-- ── Filtros (query / update / delete) ── -->
      <template v-if="hasFilter">
        <div class="h-px bg-n-weak -mx-4" />
        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.FILTERS') }}</span>

          <div class="flex flex-col gap-3">
            <template v-for="(cond, i) in conditions" :key="i">

              <!-- AND/OR connector -->
              <div v-if="i > 0" class="flex items-center gap-2">
                <div class="h-px flex-1 bg-n-weak" />
                <div class="multiselect-wrap--small w-16">
                  <MultiselectDropdown
                    :options="QUERY_OPTIONS"
                    :selected-item="QUERY_OPTIONS.find(o => o.id === (conditions[i - 1].query_operator || 'and'))"
                    :has-thumbnail="false"
                    :hide-search="true"
                    @select="v => patchCondition(i - 1, { query_operator: v?.id ?? 'and' })"
                  />
                </div>
                <div class="h-px flex-1 bg-n-weak" />
              </div>

              <!-- Condition card -->
              <div class="rounded-xl border border-n-weak bg-n-solid-1">

                <!-- Row 1: campo + delete -->
                <div class="flex items-center gap-1.5 px-2 py-1.5 bg-n-solid-2 border-b border-n-weak rounded-t-xl">
                  <span class="text-[11px] font-medium text-n-slate-9 shrink-0">{{ t('FLOW_BUILDER.COMMON.IF') }}</span>
                  <div class="multiselect-wrap--small flex-1">
                    <MultiselectDropdown
                      :options="fieldOptions"
                      :selected-item="fieldOptions.find(o => o.id === cond.field) ?? null"
                      :multiselector-placeholder="t('FLOW_BUILDER.COMMON.FIELD')"
                      :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                      :has-thumbnail="false"
                      @select="v => onSelectConditionField(i, v)"
                    />
                  </div>
                  <button
                    type="button"
                    class="i-lucide-x size-3 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors"
                    @click="removeCondition(i)"
                  />
                </div>

                <!-- Row 2: operador + valor -->
                <div class="flex items-start gap-1.5 px-2 py-1.5">
                  <div class="multiselect-wrap--small flex-1 min-w-0">
                    <MultiselectDropdown
                      :options="opOptions(cond.field)"
                      :selected-item="opOptions(cond.field).find(o => o.id === cond.operator) ?? null"
                      :multiselector-placeholder="t('FLOW_BUILDER.COMMON.OPERATOR')"
                      :has-thumbnail="false"
                      :hide-search="true"
                      @select="v => onSelectConditionOperator(i, v?.id ?? 'equal_to', cond.field)"
                    />
                  </div>

                  <div v-if="needsValue(cond)" class="relative flex-1 min-w-0">
                    <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                      <input
                        type="text"
                        class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                        :value="cond.value"
                        :placeholder="t('FLOW_BUILDER.COMMON.VALUE_PLACEHOLDER')"
                        @input="patchCondition(i, { value: $event.target.value })"
                      />
                      <button
                        type="button"
                        class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
                        :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
                        @click.stop="togglePicker(`cond_${i}`)"
                      >
                        <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
                      </button>
                    </div>
                    <div
                      v-if="openPickerKey === `cond_${i}`"
                      class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
                    >
                      <div class="px-2 pt-2 pb-1">
                        <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                          <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                          <input v-model="varSearch" type="text" class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9" :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')" autofocus />
                        </div>
                      </div>
                      <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                        <button v-for="v in filteredVars" :key="v.key" type="button" class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors" @click="insertVarInCondition(i, v.key)">{{ resolveLabel(v.label) }}</button>
                        <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <Button ghost slate sm icon="i-lucide-plus" :label="t('FLOW_BUILDER.COMMON.ADD_FILTER')" class="self-start" @click="addCondition" />
          </div>
        </div>
      </template>

      <!-- ── Mapeamento de campos (insert / update) ── -->
      <template v-if="hasMapping">
        <div class="h-px bg-n-weak -mx-4" />
        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.FIELDS') }}</span>

          <div class="flex flex-col gap-2">
            <div v-for="(mapping, i) in mappings" :key="i" class="rounded-xl border border-n-weak bg-n-solid-1">

              <!-- Campo (coluna da tabela) -->
              <div class="flex items-center gap-1.5 px-2 py-1.5 bg-n-solid-2 border-b border-n-weak rounded-t-xl">
                <div class="multiselect-wrap--small flex-1">
                  <MultiselectDropdown
                    :options="fieldOptions"
                    :selected-item="fieldOptions.find(o => o.id === mapping.field) ?? null"
                    :multiselector-placeholder="t('FLOW_BUILDER.COMMON.FIELD')"
                    :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
                    :has-thumbnail="false"
                    @select="v => patchMapping(i, { field: v?.id ?? '' })"
                  />
                </div>
                <button
                  type="button"
                  class="i-lucide-x size-3 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors"
                  @click="removeMapping(i)"
                />
              </div>

              <!-- Valor -->
              <div class="px-2 py-1.5">
                <div class="relative">
                  <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                    <input
                      type="text"
                      class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                      :value="mapping.value"
                      :placeholder="t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.VALUE_OR_VARIABLE_PLACEHOLDER')"
                      @input="patchMapping(i, { value: $event.target.value })"
                    />
                    <button
                      type="button"
                      class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
                      :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
                      @click.stop="togglePicker(`map_${i}`)"
                    >
                      <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
                    </button>
                  </div>
                  <div
                    v-if="openPickerKey === `map_${i}`"
                    class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
                  >
                    <div class="px-2 pt-2 pb-1">
                      <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                        <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                        <input v-model="varSearch" type="text" class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9" :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')" autofocus />
                      </div>
                    </div>
                    <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
                      <button v-for="v in filteredVars" :key="v.key" type="button" class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors" @click="insertVarInMapping(i, v.key)">{{ resolveLabel(v.label) }}</button>
                      <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Button ghost slate sm icon="i-lucide-plus" :label="t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.ADD_FIELD')" class="self-start" @click="addMapping" />
          </div>
        </div>
      </template>

      <div class="h-px bg-n-weak -mx-4" />

      <!-- Salvar resultado (query / insert) -->
      <div v-if="isQuery || isInsert" class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ isQuery ? t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.SAVE_RESULT_IN_VARIABLE') : t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.SAVE_INSERTED_ID_IN_VARIABLE') }}
        </span>
        <input
          type="text"
          class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
          :placeholder="isQuery ? 'resultado_tabela' : 'novo_id'"
          :value="modelValue?.variable_name ?? ''"
          @input="update({ variable_name: $event.target.value })"
        />
        <p class="text-xs text-n-slate-9">
          {{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.USE_HINT_BEFORE') }} <code class="text-n-slate-11">{{ resultVarToken }}</code> {{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.USE_HINT_AFTER') }}
        </p>
      </div>

      <!-- Limite (query) -->
      <div v-if="isQuery" class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.ROWS_LIMIT') }}</span>
        <input
          type="number"
          min="1"
          max="1000"
          class="w-24 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          :value="modelValue?.limit ?? 10"
          @input="update({ limit: parseInt($event.target.value) || 10 })"
        />
      </div>

      <!-- Outputs info (query) -->
      <div v-if="isQuery" class="flex flex-col gap-1.5 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
        <div class="flex items-center gap-2 text-xs text-n-slate-9">
          <span class="inline-block size-2 rounded-full bg-n-teal-9 shrink-0" />
          <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.FOUND') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.FOUND_HINT') }}</span>
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-9">
          <span class="inline-block size-2 rounded-full bg-n-ruby-9 shrink-0" />
          <span><strong class="text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.NOT_FOUND') }}</strong> {{ t('FLOW_BUILDER.ACTION_FORMS.DATATABLE.NOT_FOUND_HINT') }}</span>
        </div>
      </div>

    </template>
  </div>
</template>
